import useVisibleProjects from "../hooks/useVisibleProjects";
// src/popup/PopupShift.js
// Ändring: popupen hämtar nu både godkänd övertid och godkänd frånvaro direkt.
// När en ansökan återkallas och får status "pending" tas den automatiskt bort ur popupens lokala visning,
// eftersom tidigare approvedFromRequest-rader rensas innan nya godkända ansökningar slås ihop.

import React, { useEffect, useMemo, useRef, useState } from "react";
import MultiDatePopup from "./MultiDatePopup";
import AddOvertimePopup from "./AddOvertimePopup";
import AddAbsenceTypesPopup from "./AddAbsencePopup";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { FaUsers, FaUserCheck, FaUndo, FaLock, FaLockOpen, FaCheckCircle } from "react-icons/fa";
import { filterEmployeesForSchema, mergeSchedulingEmployees, readSchedulingOwner } from "../utils/schedulingPopupData";

export default function PopupShift({
  isOpen,
  onClose,
  selectedDate,
  selectedShift,
  selectedSchema,
  companyRootUid,
  payrollLocks = null,
  isDatePublished = () => false,
  preselectedEmployeeId,
  preselectedEmployeeName,
  preselectedObemannad = false,
  preselectedProject = null,
}) {
  const { currentUser, companyAccountUid } = useAuth();

  const effectiveCompanyUid =
    companyRootUid || companyAccountUid || currentUser?.uid;

  const emptyEmployee = useMemo(
 () => ({
  employeeId: "",
  isObemannad: false,

  overtime: [],
  absences: [],
  breaks: [],

  name: "",

  isBooked: false,
  isApproved: false,

  bookingId: null,
  originalEmployeeId: null,

  bookingUserId: "",
  bookingUserName: "",
}),
    []
  );

  const [formData, setFormData] = useState({
    employees: [emptyEmployee],
    project: "",
    projectId: "",
    date: "",
    startTime: "",
    endTime: "",
    message: "",
  });

  const approvedSingleTime = formData.employees.length === 1 &&
    formData.employees[0]?.payrollTime?.source === "punched"
      ? formData.employees[0].payrollTime : null;

  const [expandedEmployeeIndex, setExpandedEmployeeIndex] = useState(null);
  const [isMultiDatePopupOpen, setIsMultiDatePopupOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [multiDateSelection, setMultiDateSelection] = useState(null);
  const [datesLocked, setDatesLocked] = useState(false);
  const [dateDrafts, setDateDrafts] = useState({});

  const readDateDraft = (data) => ({
    startTime: data.startTime,
    endTime: data.endTime,
    project: data.project,
    projectId: data.projectId,
    message: data.message,
    employees: data.employees.map((employee) => ({
      ...employee,
      breaks: (employee.breaks || []).map((entry) => ({ ...entry })),
      overtime: (employee.overtime || []).map((entry) => ({ ...entry })),
      absences: (employee.absences || []).map((entry) => ({ ...entry })),
    })),
  });

  const getDateDraft = (date) =>
    date === formData.date || datesLocked ? readDateDraft(formData) : dateDrafts[date];

  const selectEditingDate = (date) => {
    if (date === formData.date) return;
    const draft = getDateDraft(date);
    setDateDrafts((prev) => ({ ...prev, [formData.date]: readDateDraft(formData) }));
    setFormData((prev) => ({ ...prev, ...readDateDraft(draft || prev), date }));
    setActiveEmployeePopup((prev) => ({ ...prev, show: false }));
    setShowEmployeeDropdown(null);
    setShowProjectDropdown(false);
  };

  const toggleDatesLocked = () => {
    setDateDrafts(Object.fromEntries((multiDateSelection?.dates || []).map((date) => [
      date, readDateDraft(formData),
    ])));
    setDatesLocked((prev) => !prev);
  };
  const [showOvertimePopup, setShowOvertimePopup] = useState(false);
  const [showAbsenceTypesPopup, setShowAbsenceTypesPopup] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(null);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [searchEmployeeTerm, setSearchEmployeeTerm] = useState("");
  const [searchProjectTerm, setSearchProjectTerm] = useState("");

  const [employedEmployees, setEmployees] = useState([]);
  const [ownerEmployee, setOwnerEmployee] = useState(null);
  const [schemaMembership, setSchemaMembership] = useState({ schemaId: null, employeeIds: null, error: "" });
  const employees = useMemo(() => mergeSchedulingEmployees(employedEmployees, ownerEmployee),
    [employedEmployees, ownerEmployee]);
  const schemaEmployeeIds = schemaMembership.schemaId === selectedSchema
    ? schemaMembership.employeeIds : null;
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [overtimeRules, setOvertimeRules] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [shiftBookings, setShiftBookings] = useState([]);
  const deletionBookingsRef = useRef(null);
  const [pendingBookingChanges, setPendingBookingChanges] = useState([]);
  const [approvedOvertimeRequests, setApprovedOvertimeRequests] = useState([]);
  const [approvedAbsenceRequests, setApprovedAbsenceRequests] = useState([]);

  const [activeEmployeePopup, setActiveEmployeePopup] = useState({
    employeeIndex: null,
    type: null,
    show: false,
    editingIndex: null,
    tempData: {
      startTime: "",
      endTime: "",
      appliesToWholeShift: false,
      type: "",
      overtimeRuleId: "",
      overtimeRuleName: "",
      absenceTypeId: "",
      absenceTypeName: "",
      absencePercent: 0,
    },
  });

  const getMonday = (date) => {
    const d = new Date(date);
    const day = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - day);
    return d;
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return "";
    const employee = employees.find((emp) => emp.id === employeeId);
    return employee?.name || "";
  };

  // FIXAD: Använder lokal tid istället för UTC
  const normalizeDateToInput = (dateValue) => {
    if (!dateValue) return "";

    // Om det redan är en YYYY-MM-DD sträng, returnera direkt
    if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    try {
      let parsedDate;
      
      if (dateValue instanceof Date) {
        parsedDate = dateValue;
      } else if (typeof dateValue?.toDate === "function") {
        parsedDate = dateValue.toDate();
      } else {
        parsedDate = new Date(dateValue);
      }

      if (isNaN(parsedDate.getTime())) return "";
      
      // Använd lokal tid istället för UTC
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      return "";
    }
  };

  const resolveEmployeeName = (employeeData, index = 0, shift = null) => {
    if (!employeeData) return "Anställd saknas";

    if (employeeData.isObemannad) return "Obemannad";

    const matchedEmployee = employeeData.employeeId
      ? employees.find((emp) => emp.id === employeeData.employeeId)
      : null;

    if (matchedEmployee?.name) return matchedEmployee.name;
    if (employeeData?.name) return employeeData.name;
    if (employeeData?.employee) return employeeData.employee;
    if (shift?.employee && index === 0) return shift.employee;
    if (employeeData?.employeeId) {
      const fallbackName = getEmployeeName(employeeData.employeeId);
      if (fallbackName) return fallbackName;
    }

    return "";
  };

  const getEmployeeByEmail = (email) => {
    if (!email) return null;
    const normalizedEmail = String(email).trim().toLowerCase();

    return employees.find(
      (emp) => String(emp.email || "").trim().toLowerCase() === normalizedEmail
    );
  };

  const findEmployeeIndexForRequest = (employeesArray, request) => {
    const possibleEmployeeId =
      request.employeeId ||
      request.userId ||
      request.uid ||
      getEmployeeByEmail(request.userEmail)?.id ||
      getEmployeeByEmail(request.email)?.id;

    if (possibleEmployeeId) {
      const byId = employeesArray.findIndex(
        (emp) => emp.employeeId === possibleEmployeeId
      );
      if (byId !== -1) return byId;
    }

    const possibleEmail = String(request.userEmail || request.email || "")
      .trim()
      .toLowerCase();

    if (possibleEmail) {
      const byEmail = employeesArray.findIndex((emp) => {
        const matchedEmployee = employees.find(
          (employee) => employee.id === emp.employeeId
        );
        return (
          String(matchedEmployee?.email || "").trim().toLowerCase() ===
          possibleEmail
        );
      });
      if (byEmail !== -1) return byEmail;
    }

    return -1;
  };

  const buildApprovedOvertimeEntry = (request) => {
    const matchingRule = request.overtimeRuleId
      ? overtimeRules.find((rule) => rule.id === request.overtimeRuleId)
      : null;

    return {
      overtimeRequestId: request.id,
      startTime: request.startTime || "",
      endTime: request.endTime || "",
      appliesToWholeShift: !!request.appliesToWholeShift,
      overtimeRuleId:
        request.overtimeRuleId || matchingRule?.id || "approved-request",
      overtimeRuleName:
        request.overtimeRuleName || matchingRule?.name || "Godkänd övertid",
      percentage: Number(request.percentage ?? matchingRule?.percentage ?? 0),
      hours: Number(request.hours || 0),
      approvedFromRequest: true,
    };
  };

  const buildApprovedAbsenceEntry = (request) => {
    const matchingType = request.absenceTypeId
      ? absenceTypes.find((type) => type.id === request.absenceTypeId)
      : null;

    return {
      absenceRequestId: request.id,
      startTime: request.startTime || "",
      endTime: request.endTime || "",
      appliesToWholeShift: !!request.appliesToWholeShift,
      type: request.absenceTypeName || matchingType?.name || "Godkänd frånvaro",
      absenceTypeId: request.absenceTypeId || matchingType?.id || "approved-request",
      absenceTypeName:
        request.absenceTypeName || matchingType?.name || "Godkänd frånvaro",
      absencePercent: Number(
        request.absencePercent ??
          request.salaryPercent ??
          matchingType?.salaryPercent ??
          0
      ),
      hours: Number(request.hours || 0),
      approvedFromRequest: true,
    };
  };

  const applyApprovedRequestsToEmployees = (
    baseEmployees,
    overtimeRequests = approvedOvertimeRequests,
    absenceRequests = approvedAbsenceRequests
  ) => {
    const employeesArray = (Array.isArray(baseEmployees) ? baseEmployees : []).map(
      (emp) => ({
        ...emptyEmployee,
        ...emp,
        overtime: Array.isArray(emp.overtime)
          ? emp.overtime.filter((entry) => !entry.approvedFromRequest)
          : [],
        absences: Array.isArray(emp.absences)
          ? emp.absences.filter((entry) => !entry.approvedFromRequest)
          : [],
        breaks: Array.isArray(emp.breaks) ? [...emp.breaks] : [],
      })
    );

    overtimeRequests.forEach((request) => {
      const employeeIndex = findEmployeeIndexForRequest(employeesArray, request);
      if (employeeIndex === -1) return;

      const currentEmployee = employeesArray[employeeIndex];
      const currentOvertime = Array.isArray(currentEmployee.overtime)
        ? [...currentEmployee.overtime]
        : [];

      const alreadyExists = currentOvertime.some(
        (entry) => entry.overtimeRequestId === request.id
      );

      if (alreadyExists) return;

      employeesArray[employeeIndex] = {
        ...currentEmployee,
        overtime: [...currentOvertime, buildApprovedOvertimeEntry(request)],
      };
    });

    absenceRequests.forEach((request) => {
      const employeeIndex = findEmployeeIndexForRequest(employeesArray, request);
      if (employeeIndex === -1) return;

      const currentEmployee = employeesArray[employeeIndex];
      const currentAbsences = Array.isArray(currentEmployee.absences)
        ? [...currentEmployee.absences]
        : [];

      const alreadyExists = currentAbsences.some(
        (entry) => entry.absenceRequestId === request.id
      );

      if (alreadyExists) return;

      employeesArray[employeeIndex] = {
        ...currentEmployee,
        absences: [...currentAbsences, buildApprovedAbsenceEntry(request)],
      };
    });

    return employeesArray;
  };

  useEffect(() => {
    if (isOpen && !selectedShift && preselectedObemannad) {
      const alreadyHasObemannad = formData.employees.some(
        (employee) => employee.isObemannad === true
      );

      if (!alreadyHasObemannad) {
        const emptyIndex = formData.employees.findIndex(
          (employee) => !employee.employeeId && !employee.isObemannad
        );
        const obemannadEmployee = {
          ...emptyEmployee,
          employeeId: "",
          name: "Obemannad",
          isObemannad: true,
        };

        setFormData((prev) => {
          if (emptyIndex === -1) {
            return {
              ...prev,
              employees: [...prev.employees, obemannadEmployee],
            };
          }

          const updatedEmployees = [...prev.employees];
          updatedEmployees[emptyIndex] = obemannadEmployee;
          return { ...prev, employees: updatedEmployees };
        });
      }
    }
  }, [
    isOpen,
    selectedShift,
    preselectedObemannad,
    formData.employees,
    emptyEmployee,
  ]);

  useEffect(() => {
    if (
      isOpen &&
      !selectedShift &&
      !preselectedObemannad &&
      preselectedEmployeeId &&
      preselectedEmployeeName &&
      schemaEmployeeIds?.includes(preselectedEmployeeId)
    ) {
      const alreadyExists = formData.employees.some(
        (emp) => emp.employeeId === preselectedEmployeeId
      );

      if (!alreadyExists) {
        const emptyIndex = formData.employees.findIndex(
          (emp) => !emp.employeeId && !emp.isObemannad
        );

        const obemannadIndex = formData.employees.findIndex(
          (emp) => emp.isObemannad === true
        );

        const targetIndex = emptyIndex !== -1 ? emptyIndex : obemannadIndex;

        if (targetIndex !== -1) {
          const updated = [...formData.employees];
          updated[targetIndex] = {
            ...updated[targetIndex],
            employeeId: preselectedEmployeeId,
            name: preselectedEmployeeName,
            isObemannad: false,
            isBooked: false,
            isApproved: false,
            bookingId: null,
            originalEmployeeId: null,
          };
          setFormData((prev) => ({ ...prev, employees: updated }));
        } else if (formData.employees.length < 50) {
          const newEmployee = {
            employeeId: preselectedEmployeeId,
            name: preselectedEmployeeName,
            isObemannad: false,
            overtime: [],
            absences: [],
            breaks: [],
            isBooked: false,
            isApproved: false,
            bookingId: null,
            originalEmployeeId: null,
          };
          setFormData((prev) => ({
            ...prev,
            employees: [...prev.employees, newEmployee],
          }));
        }
      }
    }
  }, [
    isOpen,
    selectedShift,
    preselectedObemannad,
    preselectedEmployeeId,
    preselectedEmployeeName,
    schemaEmployeeIds,
    formData.employees,
  ]);

  useEffect(() => {
    deletionBookingsRef.current = null;
    if (!isOpen || !selectedShift?.id || !effectiveCompanyUid) return;

    const bookingsRef = collection(db, "users", effectiveCompanyUid, "bookings");
    const q = query(
      bookingsRef,
      where("originalShiftId", "==", selectedShift.id)
    );

    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snapshot) => {
        // Only reuse a complete server snapshot, never a partial local cache.
        deletionBookingsRef.current = snapshot.metadata.fromCache ? null : {
          companyUid: effectiveCompanyUid,
          shiftId: selectedShift.id,
          snapshot,
        };
        const bookingsList = [];

        snapshot.forEach((bookingDoc) => {
          const bookingData = bookingDoc.data();
          if (!["approved", "reserved"].includes(bookingData.status)) return;
          bookingsList.push({
            id: bookingDoc.id,
            originalShiftId: bookingData.originalShiftId,
            participantIndex: bookingData.participantIndex,
            userId: bookingData.userId,
            userName: bookingData.userName,
            status: bookingData.status,
            bookedAt: bookingData.bookedAt || bookingData.createdAt,
          });
        });
        setShiftBookings(bookingsList);
      },
      (error) => {
        deletionBookingsRef.current = null;
        console.error("Fel vid hämtning av bokningar:", error);
      }
    );

    return () => {
      deletionBookingsRef.current = null;
      unsubscribe();
    };
  }, [isOpen, selectedShift?.id, effectiveCompanyUid]);

  useEffect(() => {
    if (!isOpen || !selectedShift?.id || !effectiveCompanyUid) {
      setApprovedOvertimeRequests([]);
      return;
    }

    const overtimeRequestsRef = collection(
      db,
      "users",
      effectiveCompanyUid,
      "overtimeRequests"
    );

    const q = query(
      overtimeRequestsRef,
      where("shiftId", "==", selectedShift.id),
      where("status", "==", "approved")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((requestDoc) => ({
          id: requestDoc.id,
          ...requestDoc.data(),
        }));

        setApprovedOvertimeRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkänd övertid:", error);
      }
    );

    return () => unsubscribe();
  }, [isOpen, selectedShift?.id, effectiveCompanyUid]);

  useEffect(() => {
    if (!isOpen || !selectedShift?.id || !effectiveCompanyUid) {
      setApprovedAbsenceRequests([]);
      return;
    }

    const absenceRequestsRef = collection(
      db,
      "users",
      effectiveCompanyUid,
      "absenceRequests"
    );

    const q = query(
      absenceRequestsRef,
      where("shiftId", "==", selectedShift.id),
      where("status", "==", "approved")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((requestDoc) => ({
          id: requestDoc.id,
          ...requestDoc.data(),
        }));

        setApprovedAbsenceRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkänd frånvaro:", error);
      }
    );

    return () => unsubscribe();
  }, [isOpen, selectedShift?.id, effectiveCompanyUid]);

  useEffect(() => {
    if (!effectiveCompanyUid) return;

    const employedRef = collection(db, "users", effectiveCompanyUid, "employed");

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();

          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          } else if (userData.userName) {
            fullName = userData.userName;
          }

          users.push({
            id: userDoc.id,
            ...userData,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "",
          });
        });

        users.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setEmployees(users);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
      }
    );

    return () => unsubscribe();
  }, [effectiveCompanyUid]);

  useEffect(() => {
    if (!isOpen || !effectiveCompanyUid) {
      setOwnerEmployee(null);
      return;
    }
    return onSnapshot(doc(db, "users", effectiveCompanyUid),
      (snapshot) => setOwnerEmployee(readSchedulingOwner(snapshot)),
      (error) => {
        console.error("Fel vid hämtning av företagsägaren:", error);
        setOwnerEmployee(null);
      });
  }, [isOpen, effectiveCompanyUid]);

  useEffect(() => {
    if (!isOpen || !effectiveCompanyUid || !selectedSchema) {
      setSchemaMembership({ schemaId: null, employeeIds: null, error: "" });
      return;
    }
    setSchemaMembership({ schemaId: selectedSchema, employeeIds: null, error: "" });
    return onSnapshot(doc(db, "users", effectiveCompanyUid, "Scheduling", selectedSchema),
      (snapshot) => {
        const ids = snapshot.exists() && Array.isArray(snapshot.data()?.visibleEmployeeIds)
          ? snapshot.data().visibleEmployeeIds : [];
        setSchemaMembership({ schemaId: selectedSchema, employeeIds: ids, error: "" });
      },
      (error) => {
        console.error("Fel vid hämtning av schemats användare:", error);
        setSchemaMembership({ schemaId: selectedSchema, employeeIds: null, error: "Kunde inte hämta användarna i schemat." });
      });
  }, [isOpen, effectiveCompanyUid, selectedSchema]);

  useEffect(() => {
    if (!effectiveCompanyUid) return;

    const projectsRef = collection(db, "users", effectiveCompanyUid, "projects");

    const unsubscribe = onSnapshot(
      projectsRef,
      (snapshot) => {
        const projectList = [];

        snapshot.forEach((projectDoc) => {
          const data = projectDoc.data();

          projectList.push({
            id: projectDoc.id,
            name: data.name || "Projekt saknas",
          });
        });

        projectList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setProjects(projectList);
      },
      (error) => {
        console.error("Fel vid hämtning av projekt:", error);
      }
    );

    return () => unsubscribe();
  }, [effectiveCompanyUid]);

  useEffect(() => {
    if (!effectiveCompanyUid) return;

    const overtimeRulesRef = collection(
      db,
      "users",
      effectiveCompanyUid,
      "overtimeRules"
    );

    const unsubscribe = onSnapshot(
      overtimeRulesRef,
      (snapshot) => {
        const rules = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));
        rules.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOvertimeRules(rules);
      },
      (error) => {
        console.error("Fel vid hämtning av övertidsregler:", error);
      }
    );

    return () => unsubscribe();
  }, [effectiveCompanyUid]);

  useEffect(() => {
    if (!effectiveCompanyUid) return;

    const absenceTypesRef = collection(
      db,
      "users",
      effectiveCompanyUid,
      "absenceTypes"
    );

    const unsubscribe = onSnapshot(
      absenceTypesRef,
      (snapshot) => {
        const types = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));
        types.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setAbsenceTypes(types);
      },
      (error) => {
        console.error("Fel vid hämtning av frånvarotyper:", error);
      }
    );

    return () => unsubscribe();
  }, [effectiveCompanyUid]);

  const getTitle = () => {
    return selectedShift ? "Redigera pass" : "Skapa pass";
  };

  useEffect(() => {
    if (isOpen && selectedShift) {
      const loadShiftData = async () => {
        const timeParts = (selectedShift.time || "").split(" - ");
        const startTime = selectedShift.startTime || timeParts[0] || "";
        const endTime = selectedShift.endTime || timeParts[1] || "";

        let shiftDate = "";
        if (selectedShift.date) {
          shiftDate = normalizeDateToInput(selectedShift.date);
        } else if (selectedDate) {
          shiftDate = normalizeDateToInput(selectedDate);
        } else {
          const today = new Date();
          const monday = getMonday(today);
          const shiftDay = new Date(monday);
          shiftDay.setDate(monday.getDate() + (selectedShift.day || 0));
          shiftDate = normalizeDateToInput(shiftDay);
        }

        let employeesData = [];

        if (selectedShift.employees && Array.isArray(selectedShift.employees)) {
          employeesData = selectedShift.employees.map((emp, index) => {
            return {
              ...emptyEmployee,
              ...emp,
              employeeId: emp.employeeId || "",
              isObemannad: emp.isObemannad || false,
              overtime: emp.overtime || [],
              absences: emp.absences || [],
              breaks: emp.breaks || [],
              name: resolveEmployeeName(emp, index, selectedShift),
              isBooked: false,
              isApproved: false,
              bookingId: null,
              originalEmployeeId: null,
            };
          });
        } else if (selectedShift.employeeId || selectedShift.isObemannad) {
          const singleEmployeeRow = {
            ...emptyEmployee,
            employeeId: selectedShift.employeeId || "",
            isObemannad: selectedShift.isObemannad || false,
            overtime: selectedShift.overtime || [],
            absences: selectedShift.absences || [],
            breaks: selectedShift.breaks || [],
            payrollTime: selectedShift.payrollTime || null,
            name: resolveEmployeeName(
              {
                employeeId: selectedShift.employeeId || "",
                isObemannad: selectedShift.isObemannad || false,
                name: selectedShift.name || selectedShift.employee || "",
                employee: selectedShift.employee || "",
              },
              0,
              selectedShift
            ),
            isBooked: false,
            isApproved: false,
            bookingId: null,
            originalEmployeeId: null,
          };
          employeesData = [singleEmployeeRow];
        } else {
          employeesData = [{ ...emptyEmployee }];
        }

        setFormData({
          employees: applyApprovedRequestsToEmployees(
            employeesData,
            approvedOvertimeRequests,
            approvedAbsenceRequests
          ),
          project: selectedShift.project || "",
          projectId: selectedShift.projectId || "",
          date: shiftDate,
          startTime,
          endTime,
          message: selectedShift.message || "",
        });
      };

      loadShiftData();
    }
  }, [
    isOpen,
    selectedShift,
    selectedDate,
    employees,
    approvedOvertimeRequests,
    approvedAbsenceRequests,
    overtimeRules,
    absenceTypes,
    emptyEmployee,
  ]);

  useEffect(() => {
    if (!isOpen || !selectedShift?.id) return;

    setFormData((prev) => ({
      ...prev,
      employees: applyApprovedRequestsToEmployees(
        prev.employees,
        approvedOvertimeRequests,
        approvedAbsenceRequests
      ),
    }));
  }, [
    isOpen,
    selectedShift?.id,
    approvedOvertimeRequests,
    approvedAbsenceRequests,
    employees,
    overtimeRules,
    absenceTypes,
  ]);

  useEffect(() => {
    if (isOpen && !selectedShift) {
      setFormData((prev) => ({
        ...prev,
        date: selectedDate ? normalizeDateToInput(selectedDate) : "",
        project: preselectedProject?.name || prev.project,
        projectId: preselectedProject?.id || prev.projectId,
      }));
    }
  }, [isOpen, selectedDate, selectedShift, preselectedProject]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        employees: [{ ...emptyEmployee }],
        project: "",
        projectId: "",
        date: "",
        startTime: "",
        endTime: "",
        message: "",
      });
      setExpandedEmployeeIndex(null);
      setIsMultiDatePopupOpen(false);
      setShowEmployeeDropdown(null);
      setShowProjectDropdown(false);
      setSearchEmployeeTerm("");
      setSearchProjectTerm("");
      setIsSaving(false);
      setShowDeleteConfirm(false);
      setMultiDateSelection(null);
      setDatesLocked(false);
      setDateDrafts({});
      setShowOvertimePopup(false);
      setShowAbsenceTypesPopup(false);
      setShiftBookings([]);
      setPendingBookingChanges([]);
      setApprovedOvertimeRequests([]);
      setApprovedAbsenceRequests([]);
      setActiveEmployeePopup({
        employeeIndex: null,
        type: null,
        show: false,
        editingIndex: null,
        tempData: {
          startTime: "",
          endTime: "",
          appliesToWholeShift: false,
          type: "",
          overtimeRuleId: "",
          overtimeRuleName: "",
          absenceTypeId: "",
          absenceTypeName: "",
          absencePercent: 0,
        },
      });
    }
  }, [isOpen, emptyEmployee]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleObemannad = (index) => {
    const updated = [...formData.employees];
    const current = updated[index];
    const nextIsObemannad = !current.isObemannad;

    updated[index] = {
      ...current,
      isObemannad: nextIsObemannad,
      employeeId: nextIsObemannad ? "" : current.employeeId,
      name: nextIsObemannad ? "Obemannad" : current.name || "",
    };

    setFormData((prev) => ({
      ...prev,
      employees: updated,
    }));

    if (showEmployeeDropdown === index) {
      setShowEmployeeDropdown(null);
    }
  };

  const addAnotherEmployee = () => {
    if (formData.employees.length < 50) {
      setFormData((prev) => ({
        ...prev,
        employees: [...prev.employees, { ...emptyEmployee }],
      }));
    }
  };

  const removeEmployee = (index) => {
    if (formData.employees.length === 1) return;

    const updated = formData.employees.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, employees: updated }));

    if (expandedEmployeeIndex === index) {
      setExpandedEmployeeIndex(null);
    } else if (expandedEmployeeIndex > index) {
      setExpandedEmployeeIndex((prev) => prev - 1);
    }

    if (showEmployeeDropdown === index) {
      setShowEmployeeDropdown(null);
    }
  };

  const toggleEmployeeExpand = (index) => {
    setExpandedEmployeeIndex((prev) => (prev === index ? null : index));
  };

  const toggleEmployeeDropdown = (index) => {
    setShowEmployeeDropdown((prev) => (prev === index ? null : index));
    setSearchEmployeeTerm("");
  };

  const selectEmployee = (index, employee, bookingMeta = {}) => {
    if (!employee?.id || (!bookingMeta.isBooked && !schemaEmployeeIds?.includes(employee.id))) return;
    if (!payrollLocksReady || isEmployeeLockedForSelection(employee.id)) return;

    setFormData((prev) => {
      const updated = [...prev.employees];
      const currentEmployee = updated[index] || {};

      updated[index] = {
        ...currentEmployee,
        employeeId: employee.id,
        name: employee.name,
        isObemannad: false,
        isBooked: bookingMeta.isBooked || false,
        isApproved: bookingMeta.isApproved || false,
        bookingId: bookingMeta.bookingId || null,
        originalEmployeeId: currentEmployee.originalEmployeeId || null,
      };

      return { ...prev, employees: updated };
    });

    setShowEmployeeDropdown(null);
    setSearchEmployeeTerm("");
  };

  const addBookedEmployeeToShiftLocal = (userId, userName, bookingId = null) => {
    let employee = employees.find((emp) => emp.id === userId);
    
    if (!employee && userName) {
      employee = employees.find((emp) => 
        emp.name?.toLowerCase() === userName.toLowerCase() ||
        emp.name?.toLowerCase().includes(userName.toLowerCase())
      );
    }
    
    if (!employee && userId && userId.includes('@')) {
      employee = employees.find((emp) => emp.email === userId);
    }
    
    if (!employee) {
      console.warn(`Kunde inte hitta ${userName || userId} i anställd-listan. Skapar temporär anställd.`);
      
      const tempEmployee = {
        id: userId,
        name: userName || userId,
        email: "",
        isTemporary: true
      };
      
      setEmployees(prev => [...prev, tempEmployee]);
      employee = tempEmployee;
    }

    const alreadySelected = formData.employees.some(
      (emp) => emp.employeeId === employee.id
    );

    if (alreadySelected) {
      return;
    }

    const obemannadIndex = formData.employees.findIndex(
      (emp) => emp.isObemannad === true
    );

    const emptyIndex = formData.employees.findIndex(
      (emp) => !emp.employeeId && !emp.isObemannad
    );

    const targetIndex = obemannadIndex !== -1 ? obemannadIndex : emptyIndex;

    if (targetIndex !== -1) {
      selectEmployee(targetIndex, employee, {
        isBooked: true,
        isApproved: true,
        bookingId,
      });
    } else if (formData.employees.length < 50) {
      setFormData((prev) => ({
        ...prev,
        employees: [
          ...prev.employees,
          {
            ...emptyEmployee,
            employeeId: employee.id,
            name: employee.name,
            isObemannad: false,
            isBooked: true,
            isApproved: true,
            bookingId,
            originalEmployeeId: null,
          },
        ],
      }));
    }
  };

  const removeEmployeeFromShiftLocal = (booking) => {
    setFormData((prev) => {
      const bookingUserId = String(booking.userId || "").trim();
      const bookingUserName = String(booking.userName || "").trim().toLowerCase();

      const employeeIndex = prev.employees.findIndex((emp) => {
        const empEmployeeId = String(emp.employeeId || "").trim();
        const empName = String(emp.name || "").trim().toLowerCase();

        return (
          emp.bookingId === booking.id ||
          empEmployeeId === bookingUserId ||
          empName === bookingUserName
        );
      });

      if (employeeIndex === -1) {
        console.warn("Hittade ingen rad att återställa för booking:", booking);
        return prev;
      }

      const updated = [...prev.employees];

      updated[employeeIndex] = {
        ...emptyEmployee,
        employeeId: "",
        name: "Obemannad",
        isObemannad: true,
        isBooked: false,
        isApproved: false,
        bookingId: null,
        originalEmployeeId: null,
        overtime: [],
        absences: [],
        breaks: [],
      };

      return {
        ...prev,
        employees: updated,
      };
    });
  };

  const toggleProjectDropdown = () => {
    setShowProjectDropdown((prev) => !prev);
    setSearchProjectTerm("");
  };

  const selectProject = (project) => {
    setFormData((prev) => ({
      ...prev,
      project: project.name,
      projectId: project.id,
    }));
    setShowProjectDropdown(false);
    setSearchProjectTerm("");
  };

  const handleOpenMultiDatePopup = () => {
    setIsMultiDatePopupOpen(true);
  };

  const handleCloseMultiDatePopup = () => {
    setIsMultiDatePopupOpen(false);
  };

  const handleSaveMultiDates = (data) => {
    setMultiDateSelection(data);
    setDatesLocked(false);
    setDateDrafts(Object.fromEntries(data.dates.map((date) => [date, readDateDraft(formData)])));
    setFormData((prev) => ({ ...prev, date: data.dates[0] || prev.date }));
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString("sv-SE", options);
  };

  const getSelectedDatesList = () => {
    if (!multiDateSelection) return [];
    return multiDateSelection.dates || [];
  };

  const payrollLocksReady = !payrollLocks ||
    (payrollLocks.ready && payrollLocks.companyUid === effectiveCompanyUid);
  const getPayrollDates = () => {
    const selectedDates = getSelectedDatesList();
    return selectedDates.length > 0 ? selectedDates : [formData.date].filter(Boolean);
  };
  const isEmployeePayrollLocked = (employeeId, date) => {
    if (!payrollLocks || !payrollLocksReady || !employeeId || !date) return false;
    const period = normalizeDateToInput(date).slice(0, 7);
    return Boolean(payrollLocks.byPeriod.get(period)?.has(String(employeeId)));
  };
  const isEmployeeLockedForSelection = (employeeId) =>
    getPayrollDates().some((date) => isEmployeePayrollLocked(employeeId, date));
  const hasLockedEmployee = getPayrollDates().some((date) =>
    (multiDateSelection ? getDateDraft(date)?.employees || [] : formData.employees).some((employee) =>
      employee.employeeId && isEmployeePayrollLocked(employee.employeeId, date)
    )
  );

  const openAddPopup = (employeeIndex, type) => {
    setActiveEmployeePopup({
      employeeIndex,
      type,
      show: true,
      editingIndex: null,
      tempData: {
        startTime: "",
        endTime: "",
        appliesToWholeShift: false,
        type: type === "absence" ? "" : undefined,
        overtimeRuleId: "",
        overtimeRuleName: "",
        absenceTypeId: "",
        absenceTypeName: "",
        absencePercent: 0,
      },
    });
  };

  const openEditPopup = (employeeIndex, type, entryIndex, entry) => {
    if (entry?.approvedFromRequest) {
      alert(
        type === "absence"
          ? "Den här frånvaron kommer från en godkänd ansökan och bör ändras från frånvaroansökan."
          : "Den här övertiden kommer från en godkänd ansökan och bör ändras från övertidsansökan."
      );
      return;
    }

    setActiveEmployeePopup({
      employeeIndex,
      type,
      show: true,
      editingIndex: entryIndex,
      tempData: {
        startTime: entry.startTime || "",
        endTime: entry.endTime || "",
        appliesToWholeShift: entry.appliesToWholeShift || false,
        type: entry.type || "",
        overtimeRuleId: entry.overtimeRuleId || "",
        overtimeRuleName: entry.overtimeRuleName || "",
        absenceTypeId: entry.absenceTypeId || "",
        absenceTypeName: entry.absenceTypeName || "",
        absencePercent: entry.absencePercent || 0,
      },
    });
  };

  const handlePopupInputChange = (field, value) => {
    setActiveEmployeePopup((prev) => ({
      ...prev,
      tempData: { ...prev.tempData, [field]: value },
    }));
  };

  const handleAppliesToWholeShiftChange = (checked) => {
    if (checked) {
      const shiftStartTime = formData.startTime;
      const shiftEndTime = formData.endTime;

      setActiveEmployeePopup((prev) => ({
        ...prev,
        tempData: {
          ...prev.tempData,
          appliesToWholeShift: true,
          startTime: shiftStartTime,
          endTime: shiftEndTime,
        },
      }));
    } else {
      setActiveEmployeePopup((prev) => ({
        ...prev,
        tempData: {
          ...prev.tempData,
          appliesToWholeShift: false,
          startTime: "",
          endTime: "",
        },
      }));
    }
  };

  const handleSavePopupEntry = () => {
    const { employeeIndex, type, editingIndex, tempData } = activeEmployeePopup;
    const {
      startTime,
      endTime,
      appliesToWholeShift,
      type: absenceType,
      overtimeRuleId,
      overtimeRuleName,
      absenceTypeId,
      absenceTypeName,
      absencePercent,
    } = tempData;

    const updatedEmployees = [...formData.employees];
    const employee = updatedEmployees[employeeIndex];
    let targetArray = [];

    switch (type) {
      case "break":
        targetArray = [...(employee.breaks || [])];
        break;
      case "overtime":
        targetArray = [...(employee.overtime || [])];
        break;
      case "absence":
        targetArray = [...(employee.absences || [])];
        break;
      default:
        return;
    }

    if (type === "absence" && !absenceTypeId) {
      alert("Vänligen välj en frånvarotyp.");
      return;
    }

    if (type === "overtime" && !overtimeRuleId) {
      alert("Vänligen välj övertidstyp.");
      return;
    }

    const newEntry = {
      startTime,
      endTime,
      appliesToWholeShift,
      approvedFromRequest: false,
      ...(type === "absence" && {
        type: absenceTypeName || absenceType,
        absenceTypeId,
        absenceTypeName,
        absencePercent,
      }),
      ...(type === "overtime" && {
        overtimeRuleId,
        overtimeRuleName,
        percentage:
          overtimeRules.find((rule) => rule.id === overtimeRuleId)?.percentage || 0,
      }),
    };

    if (editingIndex !== null) {
      targetArray[editingIndex] = newEntry;
    } else {
      targetArray.push(newEntry);
    }

    switch (type) {
      case "break":
        employee.breaks = targetArray;
        break;
      case "overtime":
        employee.overtime = targetArray;
        break;
      case "absence":
        employee.absences = targetArray;
        break;
      default:
        break;
    }

    setFormData((prev) => ({ ...prev, employees: updatedEmployees }));
    closePopup();
  };

  const closePopup = () => {
    setActiveEmployeePopup({
      employeeIndex: null,
      type: null,
      show: false,
      editingIndex: null,
      tempData: {
        startTime: "",
        endTime: "",
        appliesToWholeShift: false,
        type: "",
        overtimeRuleId: "",
        overtimeRuleName: "",
        absenceTypeId: "",
        absenceTypeName: "",
        absencePercent: 0,
      },
    });
  };

  const deleteEntry = (employeeIndex, type, entryIndex) => {
    const updatedEmployees = [...formData.employees];
    const employee = updatedEmployees[employeeIndex];
    let targetArray = [];

    switch (type) {
      case "break":
        targetArray = [...(employee.breaks || [])];
        break;
      case "overtime":
        targetArray = [...(employee.overtime || [])];
        break;
      case "absence":
        targetArray = [...(employee.absences || [])];
        break;
      default:
        return;
    }

    if (targetArray[entryIndex]?.approvedFromRequest) {
      alert(
        type === "absence"
          ? "Den här frånvaron kommer från en godkänd ansökan och kan inte tas bort här."
          : "Den här övertiden kommer från en godkänd ansökan och kan inte tas bort här."
      );
      return;
    }

    targetArray.splice(entryIndex, 1);

    switch (type) {
      case "break":
        employee.breaks = targetArray;
        break;
      case "overtime":
        employee.overtime = targetArray;
        break;
      case "absence":
        employee.absences = targetArray;
        break;
      default:
        break;
    }

    setFormData((prev) => ({ ...prev, employees: updatedEmployees }));
  };

  const validateForm = () => {
    if (!payrollLocksReady) {
      alert("Vänta medan lönestatus hämtas innan du sparar passet.");
      return false;
    }

    if (hasLockedEmployee || (selectedShift && [
      selectedShift.employeeId,
      ...(selectedShift.employees || []).map((employee) => employee.employeeId),
    ].some((employeeId) => isEmployeePayrollLocked(employeeId, selectedShift.date)))) {
      alert("En person är godkänd i Löneexport för den valda månaden. Avmarkera Godkänd där innan du ändrar eller skapar pass.");
      return false;
    }

    if (!effectiveCompanyUid) {
      alert("Kunde inte hitta företagets konto.");
      return false;
    }

    if (!selectedSchema) {
      alert("Du måste välja ett schema först.");
      return false;
    }

    if (!Array.isArray(schemaEmployeeIds)) {
      alert(schemaMembership.error || "Vänta medan schemats användare hämtas.");
      return false;
    }

    const existingEmployeeIds = new Set(
      (selectedShift?.employees || []).map((employee) => employee.employeeId).filter(Boolean)
    );
    if (selectedShift?.employeeId) existingEmployeeIds.add(selectedShift.employeeId);
    const draftsToSave = multiDateSelection
      ? getSelectedDatesList().map((date) => ({ ...getDateDraft(date), date }))
      : [formData];
    if (draftsToSave.some((draft) => draft.employees.some((employee) => employee.employeeId &&
      !schemaEmployeeIds.includes(employee.employeeId) &&
      !employee.isBooked &&
      !existingEmployeeIds.has(employee.employeeId)))) {
      alert("En vald anställd tillhör inte detta schema. Välj en användare som finns i schemat.");
      return false;
    }

    if (!multiDateSelection && !formData.date) {
      alert("Välj ett datum");
      return false;
    }

    const datesWithoutTimes = getSelectedDatesList().filter((date) => {
      const times = getDateDraft(date);
      return !times?.startTime || !times?.endTime;
    });
    if (datesWithoutTimes.length > 0) {
      alert(`Välj start- och sluttid för: ${datesWithoutTimes.join(", ")}`);
      selectEditingDate(datesWithoutTimes[0]);
      return false;
    }

    if (!formData.startTime || !formData.endTime) {
      alert("Välj start- och sluttid");
      return false;
    }

    for (const draft of draftsToSave) {
      if (!draft.project || (!selectedShift && !projects.some(project => project.id === draft.projectId))) {
        alert(`Välj ett projekt för ${draft.date}`);
        if (multiDateSelection) selectEditingDate(draft.date);
        return false;
      }
      if (!draft.employees.some((employee) => employee.employeeId || employee.isObemannad)) {
        alert(`Välj minst en anställd eller markera raden som obemannad för ${draft.date}`);
        if (multiDateSelection) selectEditingDate(draft.date);
        return false;
      }
    }

    return true;
  };

  // UPPDATERAD: createShiftObject med year och month
  const createShiftObject = (date) => {
    const draft = multiDateSelection ? getDateDraft(date) : null;
    const shiftForm = draft || formData;
    const firstRow = shiftForm.employees[0];
    const hasObemannadRow = shiftForm.employees.some(
      (employeeRow) => employeeRow.isObemannad === true
    );
    let firstEmployeeName = "Anställd saknas";

    if (firstRow?.isObemannad) {
      firstEmployeeName = "Obemannad";
    } else if (firstRow?.employeeId) {
      firstEmployeeName =
        getEmployeeName(firstRow.employeeId) ||
        firstRow.name ||
        "Anställd saknas";
    }

    const employeesWithNames = applyApprovedRequestsToEmployees(
      shiftForm.employees,
      approvedOvertimeRequests,
      approvedAbsenceRequests
    ).map((emp) => {
      if (emp.isObemannad) {
        return {
          ...emp,
          name: "Obemannad",
        };
      }

      if (emp.employeeId) {
        return {
          ...emp,
          name: getEmployeeName(emp.employeeId) || emp.name || "Anställd saknas",
        };
      }

      return {
        ...emp,
        name: emp.name || "",
      };
    });

    // Parse date för att få år och månad
    const shiftDate = new Date(date);
    const year = shiftDate.getFullYear();
    const month = shiftDate.getMonth() + 1; // Januari = 1

    return {
      employees: employeesWithNames,
      project: shiftForm.project,
      projectId: shiftForm.projectId,
      date: date,
      year: year, // LÄGG TILL: år för snabb filtrering
      month: month, // LÄGG TILL: månad för snabb filtrering (1-12)
      startTime: shiftForm.startTime,
      endTime: shiftForm.endTime,
      message: shiftForm.message || "",
      updatedAt: serverTimestamp(),
      published: selectedShift?.published || false,
      schemaId: selectedSchema,
      employee: firstEmployeeName,
      employeeId: firstRow?.isObemannad ? null : firstRow?.employeeId || null,

      // Ett pass med en obemannad rad ska visas bland självbokningsbara pass.
      isObemannad: hasObemannadRow,
      isSelfBookable: hasObemannadRow,
      bookingType: hasObemannadRow ? "self_booking" : null,

      time: `${shiftForm.startTime} - ${shiftForm.endTime}`,
    };
  };

  // UPPDATERAD: saveMultipleShifts med year och month
  const saveMultipleShifts = async () => {
    if (!currentUser?.uid) {
      throw new Error("Ingen användare inloggad");
    }

    if (!effectiveCompanyUid) {
      throw new Error("Företagets uid saknas");
    }

    const shiftsRef = collection(db, "users", effectiveCompanyUid, "shifts");
    const batch = writeBatch(db);
    const datesToSave = getSelectedDatesList();

    if (datesToSave.length === 0) {
      alert("Inga datum valda");
      return 0;
    }

    let successCount = 0;
    const errors = [];

    for (const date of datesToSave) {
      try {
        // Parse date för att få år och månad
        const shiftDate = new Date(date);
        const year = shiftDate.getFullYear();
        const month = shiftDate.getMonth() + 1;

        const shiftData = {
          ...createShiftObject(date),
          year: year, // LÄGG TILL
          month: month, // LÄGG TILL
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
        };

        const docRef = doc(shiftsRef);
        batch.set(docRef, shiftData);
        successCount++;
      } catch (error) {
        console.error(`Fel vid skapande av pass för datum ${date}:`, error);
        errors.push(`Datum ${date}: ${error.message}`);
      }
    }

    if (successCount > 0) {
      try {
        await batch.commit();
        console.log(`${successCount} pass skapade i en batch`);
      } catch (error) {
        console.error("Fel vid batch-skrivning:", error);
        throw new Error("Kunde inte spara passen. Försök igen.");
      }
    }

    if (errors.length > 0) {
      console.warn("Vissa pass kunde inte skapas:", errors);
      alert(`${successCount} pass skapades, men ${errors.length} misslyckades.`);
    }

    return successCount;
  };

  // UPPDATERAD: handleSave med year och month
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      if (!currentUser?.uid) {
        throw new Error("Ingen användare inloggad");
      }

      if (!effectiveCompanyUid) {
        throw new Error("Företagets uid saknas");
      }

      if (pendingBookingChanges.length > 0) {
        for (const change of pendingBookingChanges) {
          const bookingRef = doc(
            db,
            "users",
            effectiveCompanyUid,
            "bookings",
            change.bookingId
          );

          await updateDoc(bookingRef, {
            status: change.newStatus,
            updatedAt: serverTimestamp(),
          });
        }
        setPendingBookingChanges([]);
      }

      if (selectedShift) {
        // UPPDATERA EXISTERANDE PASS
        const shiftData = createShiftObject(formData.date);
        
        // Parse date för att få år och månad
        const shiftDate = new Date(formData.date);
        shiftData.year = shiftDate.getFullYear(); // LÄGG TILL
        shiftData.month = shiftDate.getMonth() + 1; // LÄGG TILL (1-12)

        const shiftRef = doc(
          db,
          "users",
          effectiveCompanyUid,
          "shifts",
          selectedShift.id
        );
        await updateDoc(shiftRef, shiftData);
        console.log("Pass uppdaterat:", selectedShift.id);
      } else if (multiDateSelection) {
        const savedCount = await saveMultipleShifts();
        if (savedCount > 0) {
          console.log(`${savedCount} nya pass skapade`);
        } else {
          throw new Error("Inga pass kunde skapas");
        }
      } else {
        // SKAPA NYTT PASS
        const shiftData = {
          ...createShiftObject(formData.date),
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
        };
        
        // Parse date för att få år och månad
        const shiftDate = new Date(formData.date);
        shiftData.year = shiftDate.getFullYear(); // LÄGG TILL
        shiftData.month = shiftDate.getMonth() + 1; // LÄGG TILL (1-12)

        const shiftsRef = collection(db, "users", effectiveCompanyUid, "shifts");
        const docRef = await addDoc(shiftsRef, shiftData);
        console.log("Nytt pass skapat med ID:", docRef.id);
      }

      onClose();
    } catch (error) {
      console.error("Fel vid sparande av pass:", error);
      alert("Kunde inte spara passet. Försök igen.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteShift = async () => {
    if (isSaving || !selectedShift?.id || !effectiveCompanyUid) return;
    if (!payrollLocksReady || [
      selectedShift.employeeId,
      ...(selectedShift.employees || []).map((employee) => employee.employeeId),
    ].some((employeeId) => isEmployeePayrollLocked(employeeId, selectedShift.date))) return;

    setIsSaving(true);

    try {
      const bookingsRef = collection(db, "users", effectiveCompanyUid, "bookings");
      const q = query(bookingsRef, where("originalShiftId", "==", selectedShift.id));

      const loadedBookings = deletionBookingsRef.current;
      const bookingsSnapshot =
        loadedBookings?.companyUid === effectiveCompanyUid &&
        loadedBookings?.shiftId === selectedShift.id
          ? loadedBookings.snapshot
          : await getDocs(q);

      const batch = writeBatch(db);

      bookingsSnapshot.forEach((bookingDoc) => {
        const bookingRef = doc(
          db,
          "users",
          effectiveCompanyUid,
          "bookings",
          bookingDoc.id
        );
        batch.delete(bookingRef);
      });

      const shiftRef = doc(db, "users", effectiveCompanyUid, "shifts", selectedShift.id);
      batch.delete(shiftRef);

      await batch.commit();

      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error("Fel vid radering av pass och bokningar:", error);
      alert("Kunde inte radera passet. Försök igen.");
    } finally {
      setIsSaving(false);
    }
  };

  const getUserName = (booking) => {
    if (booking.userName) return booking.userName;
    const employee = employees.find((emp) => emp.id === booking.userId);
    if (employee?.name) return employee.name;
    return booking.userId || "Okänd användare";
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "approved":
        return {
          bg: "#ecfdf5",
          border: "#a7f3d0",
          dot: "#10b981",
          text: "Godkänd",
          buttonText: "Redan bokad",
          buttonDisabled: true,
        };
      case "reserved":
        return {
          bg: "#fef3c7",
          border: "#fde68a",
          dot: "#f59e0b",
          text: "Reserverad",
          buttonText: "Boka person",
          buttonDisabled: false,
        };
      default:
        return {
          bg: "#f3f4f6",
          border: "#e5e7eb",
          dot: "#9ca3af",
          text: "Okänd",
          buttonText: "Okänd status",
          buttonDisabled: true,
        };
    }
  };

  const formatBookingDate = (date) => {
    if (!date) return "";
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString("sv-SE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "";
    }
  };

  const handleLocalBookPerson = (booking) => {
    const employee = employees.find((emp) => emp.id === booking.userId);
    const userName = employee?.name || booking.userName || "Anställd";

    const updatedBookings = shiftBookings.map((b) =>
      b.id === booking.id ? { ...b, status: "approved" } : b
    );
    setShiftBookings(updatedBookings);

    addBookedEmployeeToShiftLocal(booking.userId, userName, booking.id);

    setPendingBookingChanges((prev) => [
      ...prev.filter((change) => change.bookingId !== booking.id),
      {
        bookingId: booking.id,
        newStatus: "approved",
        oldStatus: booking.status,
      },
    ]);
  };

  const handleLocalRevokeBooking = (booking) => {
    const updatedBookings = shiftBookings.map((b) =>
      b.id === booking.id ? { ...b, status: "reserved" } : b
    );
    setShiftBookings(updatedBookings);

    removeEmployeeFromShiftLocal(booking);

    setPendingBookingChanges((prev) => [
      ...prev.filter((change) => change.bookingId !== booking.id),
      {
        bookingId: booking.id,
        newStatus: "reserved",
        oldStatus: booking.status,
      },
    ]);
  };

  const filteredEmployees = filterEmployeesForSchema(employees, schemaEmployeeIds, searchEmployeeTerm);

  const filteredProjects = projects.filter((project) =>
    (project.name || "").toLowerCase().includes(searchProjectTerm.toLowerCase())
  );

  if (!isOpen) return null;

  const selectedDatesList = getSelectedDatesList();
  const hasMultipleDates = selectedDatesList.length > 0 && !selectedShift;

  const approvedCount = shiftBookings.filter((b) => b.status === "approved").length;
  const reservedCount = shiftBookings.filter((b) => b.status === "reserved").length;
  const hasBookings = shiftBookings.length > 0;
  const hasPendingChanges = pendingBookingChanges.length > 0;

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.32)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
      padding: 16,
    },
    modal: {
      width: hasBookings ? "min(1400px, 95vw)" : "min(1100px, 92vw)",
      maxHeight: "88vh",
      overflowY: "auto",
      background: "#ffffff",
      borderRadius: 14,
      padding: 18,
      boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
    },
    title: {
      margin: "0 0 16px 0",
      fontSize: 20,
      fontWeight: 700,
      color: "#1f2937",
    },
    pendingBadge: {
      display: "inline-block",
      marginLeft: 10,
      padding: "2px 8px",
      borderRadius: 20,
      background: "#fef3c7",
      color: "#92400e",
      fontSize: 12,
      fontWeight: 500,
    },
    mainLayout: {
      display: "grid",
      gridTemplateColumns: hasBookings ? "380px 1fr" : "1fr",
      gap: 20,
      alignItems: "start",
    },
    bookingsPanel: {
      background: "#f8fafc",
      borderRadius: 12,
      border: "1px solid #e2e8f0",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      maxHeight: "calc(88vh - 80px)",
    },
    bookingsHeader: {
      padding: "16px",
      borderBottom: "1px solid #e2e8f0",
      background: "#ffffff",
    },
    bookingsTitle: {
      margin: 0,
      fontSize: 16,
      fontWeight: 700,
      color: "#1e293b",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    bookingsStats: {
      display: "flex",
      gap: 12,
      marginTop: 8,
      fontSize: 12,
      color: "#64748b",
    },
    bookingsList: {
      flex: 1,
      overflowY: "auto",
      padding: "12px",
    },
    bookingCard: (statusInfo) => ({
      background: statusInfo.bg,
      borderRadius: 10,
      border: `1px solid ${statusInfo.border}`,
      padding: "12px",
      marginBottom: "10px",
    }),
    bookingUserName: {
      margin: 0,
      fontWeight: 600,
      fontSize: 14,
      color: "#1e293b",
    },
    bookingStatus: (statusInfo) => ({
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      color: statusInfo.dot,
      marginTop: 6,
    }),
    statusDot: (color) => ({
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: color,
    }),
    bookingDate: {
      fontSize: 11,
      color: "#64748b",
      marginTop: 6,
    },
    bookingActions: {
      marginTop: 10,
      display: "flex",
      gap: 8,
    },
    actionButton: (isDisabled, isRevoke = false) => ({
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 10px",
      borderRadius: 6,
      border: isDisabled
        ? "1px solid #cbd5e1"
        : isRevoke
        ? "1px solid #ef4444"
        : "1px solid #10b981",
      background: isDisabled ? "#f1f5f9" : isRevoke ? "#ef4444" : "#10b981",
      color: isDisabled ? "#94a3b8" : "#ffffff",
      fontSize: 12,
      fontWeight: 500,
      cursor: isDisabled ? "not-allowed" : "pointer",
    }),
    rightPanel: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    layout: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 16,
      alignItems: "start",
    },
    column: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    panel: {
      background: "#f4f5f8",
      borderRadius: 12,
      padding: 16,
      border: "1px solid #e6eaf0",
    },
    sectionTitle: {
      margin: "0 0 14px 0",
      fontSize: 14,
      fontWeight: 800,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      color: "#5b6474",
    },
    employeeRow: {
      display: "grid",
      gridTemplateColumns: "44px 1fr 92px",
      gap: 10,
      alignItems: "center",
    },
    removeButton: (disabled) => ({
      height: 44,
      borderRadius: 10,
      border: `1px solid ${disabled ? "#e5e7eb" : "#f0b4b4"}`,
      background: disabled ? "#f3f4f6" : "#fff5f5",
      color: disabled ? "#9ca3af" : "#ef4444",
      fontSize: 22,
      cursor: disabled ? "not-allowed" : "pointer",
    }),
    input: {
      width: "100%",
      height: 44,
      borderRadius: 10,
      border: "1px solid #d9dee7",
      background: "#ffffff",
      padding: "0 14px",
      fontSize: 14,
      color: "#1f2937",
      boxSizing: "border-box",
      outline: "none",
    },
    selectButton: {
      height: 44,
      borderRadius: 10,
      border: "1px solid #d9dee7",
      background: "#ffffff",
      padding: "0 12px",
      fontSize: 14,
      fontWeight: 700,
      color: "#334155",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
    },
    addEmployeeButton: {
      marginTop: 6,
      height: 36,
      borderRadius: 8,
      border: "1px solid #4aa3f0",
      background: "#fff",
      color: "#4aa3f0",
      fontSize: 13,
      fontWeight: 600,
      padding: "0 12px",
      cursor: "pointer",
      alignSelf: "flex-start",
    },
    label: {
      display: "block",
      marginBottom: 8,
      fontSize: 14,
      fontWeight: 700,
      color: "#111827",
    },
    helperLink: {
      display: "inline-block",
      marginTop: 8,
      color: "#0369a1",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      background: "#f0f9ff",
      border: "1px solid #7dd3fc",
      borderRadius: 6,
      padding: "6px 10px",
    },
    timeGroup: { marginTop: 18 },
    textarea: {
      width: "100%",
      minHeight: 90,
      borderRadius: 10,
      border: "1px solid #d9dee7",
      background: "#fff",
      padding: "12px 14px",
      fontSize: 14,
      color: "#1f2937",
      resize: "vertical",
      boxSizing: "border-box",
      fontFamily: "inherit",
    },
    buttons: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
      marginTop: 18,
      borderTop: "1px solid #e6eaf0",
      paddingTop: 14,
    },
    cancelButton: {
      height: 40,
      padding: "0 14px",
      borderRadius: 10,
      border: "none",
      background: "#e5e7eb",
      color: "#1f2937",
      fontWeight: 700,
      cursor: "pointer",
    },
    saveButton: {
      height: 40,
      padding: "0 16px",
      borderRadius: 10,
      border: "none",
      background: "#2f7fd1",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
      opacity: isSaving ? 0.7 : 1,
    },
    deleteShiftButton: {
      height: 40,
      padding: "0 16px",
      borderRadius: 10,
      border: "1px solid #fecaca",
      background: "#fee2e2",
      color: "#dc2626",
      fontWeight: 700,
      cursor: "pointer",
      marginRight: "auto",
      opacity: isSaving ? 0.7 : 1,
    },
    expandedArea: {
      marginTop: 12,
      paddingTop: 12,
      borderTop: "1px solid #e5e7eb",
      display: "grid",
      gap: 10,
    },
    subCard: {
      background: "#fff",
      border: "1px solid #e6eaf0",
      borderRadius: 10,
      padding: 10,
    },
    subHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 6,
      fontWeight: 700,
      color: "#1f2937",
      fontSize: 14,
    },
    smallAddBtn: {
      border: "1px solid #4aa3f0",
      background: "#fff",
      color: "#4aa3f0",
      borderRadius: 8,
      padding: "5px 8px",
      fontSize: 12,
      fontWeight: 700,
      cursor: "pointer",
    },
    emptyText: { fontSize: 13, color: "#6b7280" },
    dropdown: {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      maxHeight: 250,
      overflowY: "auto",
      background: "#fff",
      border: "1px solid #d9dee7",
      borderRadius: 10,
      marginTop: 4,
      zIndex: 100,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
    dropdownItem: {
      padding: "10px 14px",
      cursor: "pointer",
      borderBottom: "1px solid #f1f5f9",
      fontSize: 14,
      color: "#1f2937",
      transition: "background 0.2s",
    },
    searchInput: {
      width: "100%",
      padding: "10px 14px",
      border: "none",
      borderBottom: "1px solid #e5e7eb",
      fontSize: 14,
      outline: "none",
    },
    confirmOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.35)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 3000,
      padding: 16,
    },
    confirmBox: {
      width: "min(420px, 92vw)",
      background: "#ffffff",
      borderRadius: 14,
      padding: 24,
      boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
      border: "1px solid #e5e7eb",
    },
    confirmTitle: {
      margin: "0 0 10px 0",
      fontSize: 20,
      fontWeight: 700,
      color: "#111827",
    },
    confirmText: {
      margin: "0 0 20px 0",
      fontSize: 14,
      color: "#4b5563",
      lineHeight: 1.5,
    },
    confirmButtons: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 10,
    },
    confirmCancelButton: {
      height: 40,
      padding: "0 14px",
      borderRadius: 10,
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      fontWeight: 700,
      cursor: "pointer",
    },
    confirmDeleteButton: {
      height: 40,
      padding: "0 16px",
      borderRadius: 10,
      border: "none",
      background: "#dc2626",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
      opacity: isSaving ? 0.7 : 1,
    },
    schemaInfo: {
      marginBottom: 12,
      padding: "10px 12px",
      borderRadius: 10,
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
      fontSize: 13,
      fontWeight: 600,
    },
    selectedDatesContainer: {
      marginTop: 12,
      marginBottom: 12,
      padding: "12px 16px",
      borderRadius: 10,
      background: "#f0f9ff",
      border: "1px solid #bae6fd",
    },
    selectedDatesHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 10,
    },
    selectedDatesTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: "#0369a1",
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    },
    clearButton: {
      background: "none",
      border: "none",
      color: "#0369a1",
      textDecoration: "underline",
      fontSize: 13,
      cursor: "pointer",
      padding: 0,
    },
    datesList: { display: "flex", flexWrap: "wrap", gap: 8 },
    dateCord: {
      position: "absolute",
      left: 0,
      right: 0,
      top: "50%",
      height: 1,
      background: "#94a3b8",
      pointerEvents: "none",
    },
    dateChip: {
      background: "#ffffff",
      border: "1px solid #7dd3fc",
      borderRadius: 20,
      padding: "4px 12px",
      fontSize: 13,
      color: "#0369a1",
      fontWeight: 500,
    },
    popupOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
    },
    popupContent: {
      background: "#fff",
      borderRadius: 12,
      padding: 20,
      width: "90%",
      maxWidth: "450px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    },
    popupHeader: {
      marginBottom: 16,
      fontSize: 18,
      fontWeight: 700,
      color: "#1f2937",
    },
    popupField: { marginBottom: 16 },
    popupLabel: {
      display: "block",
      marginBottom: 6,
      fontSize: 14,
      fontWeight: 600,
      color: "#4b5563",
    },
    popupInput: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      outline: "none",
      boxSizing: "border-box",
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 14,
      cursor: "pointer",
    },
    popupButtons: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      marginTop: 20,
    },
    popupCancel: {
      padding: "8px 16px",
      borderRadius: 8,
      border: "1px solid #d1d5db",
      background: "#fff",
      cursor: "pointer",
    },
    popupSave: {
      padding: "8px 16px",
      borderRadius: 8,
      border: "none",
      background: "#2f7fd1",
      color: "#fff",
      cursor: "pointer",
    },
    deleteIcon: {
      cursor: "pointer",
      color: "#ef4444",
      fontSize: 16,
      marginLeft: 8,
    },
    entryItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 8,
      fontSize: 13,
      padding: "4px 0",
      borderBottom: "1px solid #f0f0f0",
    },
    absenceTypeSelect: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      background: "#fff",
    },
    overtimeTypeSelect: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      background: "#fff",
      marginBottom: 16,
    },
    manageButton: {
      marginTop: 8,
      padding: "6px 12px",
      fontSize: 12,
      color: "#007bff",
      background: "none",
      border: "none",
      cursor: "pointer",
      textDecoration: "underline",
    },
    requestBadge: {
      marginLeft: 6,
      padding: "2px 6px",
      borderRadius: 999,
      background: "#dcfce7",
      color: "#166534",
      fontSize: 11,
      fontWeight: 700,
    },
  };

  const renderEntryList = (entries, type, employeeIndex) => {
    if (!entries || entries.length === 0) {
      return <div style={styles.emptyText}>Inga {type} tillagda ännu.</div>;
    }

    return entries.map((entry, idx) => (
      <div key={entry.overtimeRequestId || entry.absenceRequestId || idx} style={styles.entryItem}>
        <span>
          {type === "absence" ? `${entry.absenceTypeName || entry.type}: ` : ""}
          {type === "overtime" ? `${entry.overtimeRuleName || entry.overtimeRuleId}: ` : ""}
          {entry.appliesToWholeShift
            ? "Hela passet"
            : `${entry.startTime || "?"} - ${entry.endTime || "?"}`}
          {type === "overtime" && entry.percentage ? ` (${entry.percentage}% extra)` : ""}
          {type === "absence" && entry.absencePercent ? ` (${entry.absencePercent}% lön)` : ""}
          {type === "overtime" && entry.approvedFromRequest && (
            <span style={styles.requestBadge}>Godkänd ansökan</span>
          )}
          {type === "absence" && entry.approvedFromRequest && (
            <span style={styles.requestBadge}>Godkänd ansökan</span>
          )}
        </span>
        <div>
          {!entry.approvedFromRequest && (
            <>
              <span
                onClick={() => openEditPopup(employeeIndex, type, idx, entry)}
                style={{ cursor: "pointer", marginRight: 8, color: "#4aa3f0" }}
              >
                ✏️
              </span>
              <span
                onClick={() => deleteEntry(employeeIndex, type, idx)}
                style={styles.deleteIcon}
              >
                🗑️
              </span>
            </>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16 }}>
            <h2 style={styles.title}>{getTitle()}</h2>
            <span role="status" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: isDatePublished(formData.date) ? "#15803d" : "#6b7280" }}>
              {isDatePublished(formData.date) ? "Publicerat" : "Ej publicerat"}
              <FaCheckCircle aria-hidden="true" />
            </span>
          </div>

          {!selectedSchema && (
            <div style={styles.schemaInfo}>Du måste välja ett schema först.</div>
          )}

          {!effectiveCompanyUid && (
            <div style={styles.schemaInfo}>Kunde inte hitta företagets konto ännu.</div>
          )}

          {hasMultipleDates && (
            <div style={styles.selectedDatesContainer}>
              <div style={styles.selectedDatesHeader}>
                <span style={styles.selectedDatesTitle}>
                  📅 Valda datum ({selectedDatesList.length} st)
                </span>
                <button
                  onClick={() => { setMultiDateSelection(null); setDatesLocked(false); setDateDrafts({}); }}
                  style={styles.clearButton}
                >
                  Avbryt flera datum
                </button>
              </div>
              <div style={{ ...styles.datesList, alignItems: "center", gap: 0, rowGap: 8 }}>
                <div style={{ position: "relative", paddingRight: 12 }}>
                  {datesLocked && <span aria-hidden="true" style={styles.dateCord} />}
                  <button
                    type="button"
                    aria-pressed={datesLocked}
                    aria-label={datesLocked ? "Lås upp alla datum" : "Lås alla datum"}
                    title={datesLocked ? "Alla datum har samma innehåll" : "Ändra innehåll för valt datum"}
                    onClick={toggleDatesLocked}
                    style={{ ...styles.dateChip, position: "relative", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
                  >
                    {datesLocked ? <FaLock /> : <FaLockOpen />}
                    {datesLocked ? "Låst" : "Olåst"}
                  </button>
                </div>
                {selectedDatesList.map((date, index) => (
                  <div key={date} style={{ position: "relative", paddingRight: index === selectedDatesList.length - 1 ? 0 : 12 }}>
                    {datesLocked && <span aria-hidden="true" style={styles.dateCord} />}
                    <button
                      type="button"
                      aria-pressed={date === formData.date}
                      onClick={() => selectEditingDate(date)}
                      style={{
                        ...styles.dateChip,
                        position: "relative",
                        cursor: "pointer",
                        background: date === formData.date ? "#bae6fd" : "#fff",
                      }}
                    >
                      {formatDate(date)}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={styles.mainLayout}>
            {hasBookings && (
              <div style={styles.bookingsPanel}>
                <div style={styles.bookingsHeader}>
                  <div style={styles.bookingsTitle}>
                    <FaUsers size={16} color="#6323ef" />
                    Bokningar på passet
                  </div>
                  {(approvedCount > 0 || reservedCount > 0) && (
                    <div style={styles.bookingsStats}>

                    
                    </div>
                  )}
                </div>

                <div style={styles.bookingsList}>
                  {shiftBookings.map((booking, index) => {
                    const userName = getUserName(booking);
                    const statusInfo = getStatusInfo(booking.status);

                    return (
                      <div key={booking.id || index} style={styles.bookingCard(statusInfo)}>
                        <p style={styles.bookingUserName}>{userName}</p>
                        <div style={styles.bookingStatus(statusInfo)}>
                          <div style={styles.statusDot(statusInfo.dot)} />
                          <span>{statusInfo.text}</span>
                        </div>
                        {booking.bookedAt && (
                          <div style={styles.bookingDate}>📅 {formatBookingDate(booking.bookedAt)}</div>
                        )}
                        <div style={styles.bookingActions}>
                          {booking.status === "reserved" && (
                            <button
                              onClick={() => handleLocalBookPerson(booking)}
                              style={styles.actionButton(false)}
                            >
                              <FaUserCheck size={11} />
                              <span>Boka person</span>
                            </button>
                          )}
                          {booking.status === "approved" && (
                            <button
                              onClick={() => handleLocalRevokeBooking(booking)}
                              style={styles.actionButton(false, true)}
                            >
                              <FaUndo size={11} />
                              <span>Återkalla</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={styles.rightPanel}>
              <div style={styles.layout}>
                <div style={styles.column}>
                  <div style={styles.panel}>
                    <h3 style={styles.sectionTitle}>Anställda</h3>
                    {hasLockedEmployee && (
                      <p role="alert" style={{ color: "#9f1239", fontSize: 13, fontWeight: 600 }}>
                        Personen är låst för vald månad. Avmarkera Godkänd i Löneexport för att skapa pass.
                      </p>
                    )}

                    {formData.employees.map((employeeObj, index) => {
                      const onlyOne = formData.employees.length === 1;
                      const isExpanded = expandedEmployeeIndex === index;
                      const showDropdown = showEmployeeDropdown === index;
                      const isObemannad = employeeObj.isObemannad;
                      const hasRealEmployee = !isObemannad && employeeObj.employeeId;

                      const selectedEmployee = employees.find(
                        (emp) => emp.id === employeeObj.employeeId
                      );

                      const displayName = isObemannad
                        ? "Obemannad"
                        : selectedEmployee?.name ||
                          employeeObj.name ||
                          resolveEmployeeName(employeeObj, index, selectedShift);

                      return (
                        <div
                          key={index}
                          style={{
                            background: "#ffffff",
                            border: "1px solid #d9dee7",
                            borderRadius: 12,
                            padding: 14,
                            marginBottom: 14,
                            position: "relative",
                          }}
                        >
                          {employeeObj.payrollTime?.source === "punched" && (
                            <div style={{ padding: "10px 12px", marginBottom: 12, borderRadius: 8, background: "#ede9fe", color: "#5b21b6", fontSize: 13 }}>
                              <strong>Enligt stämpelklockan: {employeeObj.payrollTime.startTime} – {employeeObj.payrollTime.endTime}</strong>
                              <div style={{ marginTop: 4 }}>Tidigare schema: {employeeObj.payrollTime.scheduledStartTime || formData.startTime} – {employeeObj.payrollTime.scheduledEndTime || formData.endTime}</div>
                            </div>
                          )}
                          <div style={styles.employeeRow}>
                            <button
                              type="button"
                              onClick={() => removeEmployee(index)}
                              disabled={onlyOne}
                              style={styles.removeButton(onlyOne)}
                            >
                              −
                            </button>

                            <div style={{ position: "relative" }}>
                              <input
                                type="text"
                                placeholder="Klicka för att välja anställd"
                                value={displayName}
                                onClick={() => toggleEmployeeDropdown(index)}
                                readOnly
                                style={{
                                  ...styles.input,
                                  cursor: "pointer",
                                  backgroundColor: hasRealEmployee
                                    ? "#dcfce7"
                                    : isObemannad
                                    ? "#fef9c3"
                                    : "#f8f9fb",
                                  border: `1px solid ${
                                    hasRealEmployee
                                      ? "#86efac"
                                      : isObemannad
                                      ? "#f59e0b"
                                      : "#d9dee7"
                                  }`,
                                  fontWeight: 400,
                                  color: "#1f2937",
                                }}
                              />
                              {showDropdown && (
                                <div style={styles.dropdown}>
                                  <input
                                    type="text"
                                    placeholder="Sök anställd..."
                                    value={searchEmployeeTerm}
                                    onChange={(e) => setSearchEmployeeTerm(e.target.value)}
                                    style={styles.searchInput}
                                    autoFocus
                                  />
                                  <div
                                    onClick={() => {
                                      const updated = [...formData.employees];
                                      updated[index] = {
                                        ...updated[index],
                                        isObemannad: true,
                                        employeeId: "",
                                        name: "Obemannad",
                                        isBooked: false,
                                        isApproved: false,
                                        bookingId: null,
                                      };
                                      setFormData((prev) => ({
                                        ...prev,
                                        employees: updated,
                                      }));
                                      setShowEmployeeDropdown(null);
                                    }}
                                    style={{
                                      ...styles.dropdownItem,
                                      borderBottom: "1px solid #e5e7eb",
                                      background: "#fef3c7",
                                    }}
                                  >
                                    <div style={{ fontWeight: 600 }}>📌 Obemannad</div>
                                    <div style={{ fontSize: 12, color: "#92400e" }}>
                                      Markera som obemannad
                                    </div>
                                  </div>
                                  {filteredEmployees.length > 0 ? (
                                    filteredEmployees.map((emp) => {
                                      const isLocked = isEmployeeLockedForSelection(emp.id);
                                      const isUnavailable = !payrollLocksReady || isLocked;
                                      return (
                                      <div
                                        key={emp.id}
                                        aria-disabled={isUnavailable}
                                        onClick={isUnavailable ? undefined : () => selectEmployee(index, emp)}
                                        style={{
                                          ...styles.dropdownItem,
                                          color: isUnavailable ? "#94a3b8" : undefined,
                                          cursor: isUnavailable ? "not-allowed" : "pointer",
                                          backgroundColor:
                                            isUnavailable
                                              ? "#f1f5f9"
                                              : emp.id === employeeObj.employeeId
                                              ? "#f0f7ff"
                                              : "transparent",
                                        }}
                                        onMouseEnter={(e) => !isUnavailable &&
                                          (e.currentTarget.style.backgroundColor = "#f5f7fa")
                                        }
                                        onMouseLeave={(e) => !isUnavailable &&
                                          (e.currentTarget.style.backgroundColor =
                                            emp.id === employeeObj.employeeId
                                              ? "#f0f7ff"
                                              : "transparent")
                                        }
                                      >
                                        <div style={{ fontWeight: 600 }}>{emp.name}</div>
                                        {isLocked && <div style={{ fontSize: 12 }}>Låst för vald månad</div>}
                                      </div>
                                      );
                                    })
                                  ) : (
                                    <div style={{ ...styles.dropdownItem, color: "#9ca3af" }}>
                                      {schemaMembership.error ||
                                        (schemaEmployeeIds === null
                                          ? "Hämtar användare i schemat..."
                                          : schemaEmployeeIds.length === 0
                                            ? "Inga användare har valts för detta schema"
                                            : "Inga anställda hittades")}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => toggleEmployeeExpand(index)}
                              style={styles.selectButton}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                  transition: "0.2s",
                                }}
                              >
                                ˅
                              </span>
                              {isExpanded ? "Dölj" : "Visa"}
                            </button>
                          </div>

                          {isExpanded && (
                            <div style={styles.expandedArea}>
                              <div style={styles.subCard}>
                                <div style={styles.subHeader}>
                                  <span>Status</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleObemannad(index)}
                                    style={{
                                      ...styles.smallAddBtn,
                                      border: employeeObj.isObemannad
                                        ? "1px solid #f59e0b"
                                        : "1px solid #d1d5db",
                                      background: employeeObj.isObemannad ? "#fef3c7" : "#fff",
                                      color: employeeObj.isObemannad ? "#92400e" : "#374151",
                                    }}
                                  >
                                    {employeeObj.isObemannad ? "Obemannad ✓" : "Obemannad"}
                                  </button>
                                </div>
                                <div style={styles.emptyText}>
                                  {employeeObj.isObemannad
                                    ? "Den här raden är markerad som obemannad."
                                    : hasRealEmployee
                                    ? `✓ Anställd: ${displayName}`
                                    : "Klicka på rutan ovan för att välja anställd."}
                                </div>
                              </div>

                              <div style={styles.subCard}>
                                <div style={styles.subHeader}>
                                  <span>Raster</span>
                                  <button
                                    type="button"
                                    onClick={() => openAddPopup(index, "break")}
                                    style={styles.smallAddBtn}
                                  >
                                    + Lägg till
                                  </button>
                                </div>
                                {renderEntryList(employeeObj.breaks, "break", index)}
                              </div>

                              <div style={styles.subCard}>
                                <div style={styles.subHeader}>
                                  <span>Övertid</span>
                                  <button
                                    type="button"
                                    onClick={() => openAddPopup(index, "overtime")}
                                    style={styles.smallAddBtn}
                                  >
                                    + Lägg till
                                  </button>
                                </div>
                                {renderEntryList(employeeObj.overtime, "overtime", index)}
                              </div>

                              <div style={styles.subCard}>
                                <div style={styles.subHeader}>
                                  <span>Frånvaro</span>
                                  <button
                                    type="button"
                                    onClick={() => openAddPopup(index, "absence")}
                                    style={styles.smallAddBtn}
                                  >
                                    + Lägg till
                                  </button>
                                </div>
                                {renderEntryList(employeeObj.absences, "absence", index)}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {formData.employees.length < 50 && (
                      <button type="button" onClick={addAnotherEmployee} style={styles.addEmployeeButton}>
                        + Lägg till anställd
                      </button>
                    )}
                  </div>

                  <div style={styles.panel}>
                    <h3 style={styles.sectionTitle}>Meddelande</h3>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Lägg till ett meddelande för detta pass..."
                      style={styles.textarea}
                    />
                  </div>
                </div>

                <div style={styles.column}>
                  <div style={styles.panel}>
                    <h3 style={styles.sectionTitle}>Projekt</h3>

                    <div style={{ position: "relative" }}>
                      <input
                        name="project"
                        value={selectedShift || projects.some(project => project.id === formData.projectId) ? formData.project : ""}
                        onClick={toggleProjectDropdown}
                        readOnly
                        placeholder="Klicka för att välja projekt"
                        style={{
                          ...styles.input,
                          cursor: "pointer",
                          backgroundColor: "#f8f9fb",
                        }}
                      />

                      {showProjectDropdown && (
                        <div style={styles.dropdown}>
                          <input
                            type="text"
                            placeholder="Sök projekt..."
                            value={searchProjectTerm}
                            onChange={(e) => setSearchProjectTerm(e.target.value)}
                            style={styles.searchInput}
                            autoFocus
                          />
                          {filteredProjects.length > 0 ? (
                            filteredProjects.map((project) => (
                              <div
                                key={project.id}
                                onClick={() => selectProject(project)}
                                style={styles.dropdownItem}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor = "#f5f7fa")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor = "transparent")
                                }
                              >
                                <div style={{ fontWeight: 600 }}>{project.name}</div>
                              </div>
                            ))
                          ) : (
                            <div style={{ ...styles.dropdownItem, color: "#9ca3af" }}>
                              Inga projekt hittades
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={styles.timeGroup}>
                      <h3 style={styles.sectionTitle}>Tider</h3>

                      <div style={{ marginBottom: 18 }}>
                        <label style={styles.label}>Datum</label>
                        <input
                          type="date"
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                          style={styles.input}
                          disabled={hasMultipleDates}
                        />
                        {!selectedShift && (
                          <button type="button" onClick={handleOpenMultiDatePopup} style={styles.helperLink}>
                            Välj flera datum
                          </button>
                        )}
                      </div>

                      <div style={{ marginBottom: 18 }}>
                        {approvedSingleTime && <p style={{ color: "#5b21b6", fontSize: 12 }}>Godkänd tid enligt stämpelklockan. Ändra tidsvalet under Tidstämpling.</p>}
                        <label style={styles.label}>{approvedSingleTime ? "Godkänd starttid" : "Starttid"}</label>
                        <input
                          type="time"
                          name="startTime"
                          value={approvedSingleTime?.startTime || formData.startTime}
                          readOnly={Boolean(approvedSingleTime)}
                          onChange={handleInputChange}
                          style={styles.input}
                        />
                      </div>

                      <div>
                        <label style={styles.label}>{approvedSingleTime ? "Godkänd sluttid" : "Sluttid"}</label>
                        <input
                          type="time"
                          name="endTime"
                          value={approvedSingleTime?.endTime || formData.endTime}
                          readOnly={Boolean(approvedSingleTime)}
                          onChange={handleInputChange}
                          style={styles.input}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.buttons}>
            {selectedShift && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                style={styles.deleteShiftButton}
                disabled={isSaving}
              >
                Radera
              </button>
            )}

            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={isSaving}>
              Avbryt
            </button>

            <button
              type="button"
              onClick={handleSave}
              style={{
                ...styles.saveButton,
                opacity: isSaving || !selectedSchema || !effectiveCompanyUid || !payrollLocksReady || hasLockedEmployee ? 0.7 : 1,
                cursor:
                  isSaving || !selectedSchema || !effectiveCompanyUid || !payrollLocksReady || hasLockedEmployee
                    ? "not-allowed"
                    : "pointer",
              }}
              disabled={isSaving || !selectedSchema || !effectiveCompanyUid || !payrollLocksReady || hasLockedEmployee}
            >
              {isSaving
                ? "Sparar..."
                : selectedShift
                ? "Uppdatera"
                : hasMultipleDates
                ? `Lägg till (${selectedDatesList.length} pass)`
                : "Lägg till"}
            </button>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div style={styles.confirmOverlay} onClick={() => !isSaving && setShowDeleteConfirm(false)}>
          <div style={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.confirmTitle}>Radera pass</h3>
            <p style={styles.confirmText}>
              Är du säker på att du vill radera detta pass?
              {shiftBookings.length > 0 && (
                <span style={{ display: "block", marginTop: 8, color: "#dc2626" }}>
                  ⚠️ Detta kommer även att radera {shiftBookings.length} bokning(ar) som är kopplade till passet.
                </span>
              )}
            </p>

            <div style={styles.confirmButtons}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                style={styles.confirmCancelButton}
                disabled={isSaving}
              >
                Avbryt
              </button>

              <button
                type="button"
                onClick={handleDeleteShift}
                style={styles.confirmDeleteButton}
                disabled={isSaving}
              >
                {isSaving ? "Raderar..." : "Radera"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeEmployeePopup.show && (
        <div style={styles.popupOverlay} onClick={closePopup}>
          <div style={styles.popupContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.popupHeader}>
              {activeEmployeePopup.type === "break" && "Lägg till raster"}
              {activeEmployeePopup.type === "overtime" && "Lägg till övertid"}
              {activeEmployeePopup.type === "absence" && "Lägg till frånvaro"}
              {activeEmployeePopup.editingIndex !== null && " (Redigera)"}
            </div>

            {activeEmployeePopup.type === "overtime" && (
              <div style={styles.popupField}>
                <label style={styles.popupLabel}>Typ av övertid</label>
                <select
                  value={activeEmployeePopup.tempData.overtimeRuleId}
                  onChange={(e) => {
                    const selectedRule = overtimeRules.find((rule) => rule.id === e.target.value);
                    handlePopupInputChange("overtimeRuleId", e.target.value);
                    handlePopupInputChange("overtimeRuleName", selectedRule?.name || "");
                  }}
                  style={styles.overtimeTypeSelect}
                >
                  <option value="">Välj typ...</option>
                  {overtimeRules.map((rule) => (
                    <option key={rule.id} value={rule.id}>
                      {rule.name} ({rule.percentage}% extra)
                    </option>
                  ))}
                </select>
                <button type="button" onClick={() => setShowOvertimePopup(true)} style={styles.manageButton}>
                  Hantera övertidsregler
                </button>
              </div>
            )}

            {activeEmployeePopup.type === "absence" && (
              <div style={styles.popupField}>
                <label style={styles.popupLabel}>Typ av frånvaro</label>
                <select
                  value={activeEmployeePopup.tempData.absenceTypeId}
                  onChange={(e) => {
                    const selectedType = absenceTypes.find((type) => type.id === e.target.value);
                    handlePopupInputChange("absenceTypeId", e.target.value);
                    handlePopupInputChange("absenceTypeName", selectedType?.name || "");
                    handlePopupInputChange("absencePercent", selectedType?.salaryPercent || 0);
                  }}
                  style={styles.absenceTypeSelect}
                >
                  <option value="">Välj typ...</option>
                  {absenceTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.salaryPercent}% lön)
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.popupField}>
              <label style={styles.popupLabel}>Starttid</label>
              <input
                type="time"
                value={activeEmployeePopup.tempData.startTime}
                onChange={(e) => handlePopupInputChange("startTime", e.target.value)}
                style={styles.popupInput}
                disabled={activeEmployeePopup.tempData.appliesToWholeShift}
              />
            </div>

            <div style={styles.popupField}>
              <label style={styles.popupLabel}>Sluttid</label>
              <input
                type="time"
                value={activeEmployeePopup.tempData.endTime}
                onChange={(e) => handlePopupInputChange("endTime", e.target.value)}
                style={styles.popupInput}
                disabled={activeEmployeePopup.tempData.appliesToWholeShift}
              />
            </div>

            <div style={styles.popupField}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={activeEmployeePopup.tempData.appliesToWholeShift}
                  onChange={(e) => handleAppliesToWholeShiftChange(e.target.checked)}
                />
                Gäller hela passet
              </label>
            </div>

            <div style={styles.popupButtons}>
              <button onClick={closePopup} style={styles.popupCancel}>
                Avbryt
              </button>
              <button onClick={handleSavePopupEntry} style={styles.popupSave}>
                Spara
              </button>
            </div>
          </div>
        </div>
      )}

      <MultiDatePopup
        open={isMultiDatePopupOpen}
        onClose={handleCloseMultiDatePopup}
        onSave={handleSaveMultiDates}
      />

      <AddOvertimePopup
        isOpen={showOvertimePopup}
        onClose={() => setShowOvertimePopup(false)}
        onSave={(newRule) => {
          console.log("Overtime rule saved:", newRule);
          setShowOvertimePopup(false);
        }}
        companyRootUid={effectiveCompanyUid}
      />

      <AddAbsenceTypesPopup
        isOpen={showAbsenceTypesPopup}
        onClose={() => setShowAbsenceTypesPopup(false)}
        onSave={(newType) => {
          console.log("Absence type saved:", newType);
          setShowAbsenceTypesPopup(false);
        }}
        companyRootUid={effectiveCompanyUid}
      />
    </>
  );
}
